import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Id } from "@stoneflow/backend/convex/_generated/dataModel"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useState } from "react"
import { toast } from "sonner"
import { LogManagementModal } from "#/components/admin/log-management-modal"
import { TenantModulesSection } from "#/components/admin/tenant-modules-section"
import { RouteError } from "#/components/route-error"
import { Skeleton } from "#/components/ui/skeleton"
import { useIsPlatformOperator } from "#/hooks/useIsTenantAdmin"

export const Route = createFileRoute("/_authenticated/admin/tenants/$orgId/modules")({
	errorComponent: RouteError,
	component: ModulesTab,
})

function ModulesTab() {
	const { orgId } = Route.useParams()
	const { isOperator, isLoading } = useIsPlatformOperator()

	const access = useQuery(api.platform.access.listOrgAccess, isOperator ? { orgId } : "skip")
	const logLimit = useQuery(api.platform.billing.getLogLimitForOrg, isOperator ? { orgId } : "skip")
	const setLogEnabled = useMutation(api.platform.access.setLogEnabled)
	const [logsModalOpen, setLogsModalOpen] = useState(false)

	if (isLoading) return <Skeleton className="h-48 w-full" />
	if (!isOperator) {
		return (
			<div className="py-12 text-center text-sm text-foreground-muted">
				This tab is for platform operators.
			</div>
		)
	}
	if (!access) return <Skeleton className="h-48 w-full" />

	const handleToggleLog = async (logId: Id<"logs">, isEnabled: boolean) => {
		try {
			await setLogEnabled({ orgId, logId, isEnabled })
			toast.success(isEnabled ? "Log enabled" : "Log disabled")
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to update log")
		}
	}

	return (
		<>
			<TenantModulesSection
				logs={access.logs.map((l) => ({
					_id: l._id,
					name: l.name,
					isEnabled: l.isEnabled,
					accentColor: l.accentColor,
				}))}
				onToggleLog={handleToggleLog}
				canEditLogs
				onEditLogs={() => setLogsModalOpen(true)}
				logLimitWarning={
					logLimit?.isOverLimit && logLimit.logLimit !== null && logLimit.plan
						? {
								enabledLogCount: logLimit.enabledLogCount,
								logLimit: logLimit.logLimit,
								plan: logLimit.plan,
							}
						: null
				}
			/>
			<LogManagementModal
				orgId={orgId}
				open={logsModalOpen}
				onOpenChange={setLogsModalOpen}
				canManage={isOperator}
			/>
		</>
	)
}
